import {KeyboardKey} from '@github-ui/keyboard-key'
import {useLocalStorage} from '@github-ui/use-safe-storage'
import {Box, Button, Checkbox, FormControl, Text} from '@primer/react'
import type {DialogProps} from '@primer/react/experimental'
import {Dialog} from '@primer/react/experimental'
import {DragAndDropResources} from '../utils/strings'

type KeyboardSpecificInstructionsModalProps = {
  isOpen: boolean
  onClose: () => void
}

type KeyboardSpecificInstructionsFooterProps = Pick<KeyboardSpecificInstructionsModalProps, 'onClose'> & DialogProps

const instructionContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: '5px',
}

const instructionSx = {
  display: 'flex',
  padding: '16px',
  justifyContent: 'space-between',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.default',
}

/*
 * Key used to store the user preference to stop showing the keyboard instructions modal
 */
export const HideKeyboardSpecificInstructionsModalKey = 'hideKeyboardSpecificInstructions'

const KeyboardSpecificInstructionsFooter = ({onClose}: KeyboardSpecificInstructionsFooterProps) => {
  const [doNotShowAgain, setDoNotShowAgain] = useLocalStorage(HideKeyboardSpecificInstructionsModalKey, false)
  return (
    <Box sx={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
      <FormControl sx={{display: 'flex', alignItems: 'center', '> :first-child': {display: 'contents'}}}>
        <Checkbox checked={doNotShowAgain} onChange={() => setDoNotShowAgain(!doNotShowAgain)} />
        <FormControl.Label>{`Don't show this again`}</FormControl.Label>
      </FormControl>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
        <Button onClick={onClose}>Close</Button>
      </Box>
    </Box>
  )
}

/* Modal component that displays keyboard instructions to perform drag and drop actions.
 * Using the Primer Dialog component.
 *
 * @param isOpen - Whether the modal is open or not
 * @param onClose - Callback called when the modal is closed
 */
export const KeyboardSpecificInstructionsModal = ({isOpen, onClose}: KeyboardSpecificInstructionsModalProps) => {
  if (!isOpen) return null

  const onKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if (e.code === 'Escape') {
      onClose()
    }
  }

  return (
    <Box onKeyDown={onKeyDown}>
      <Dialog
        title="How to move objects via keyboard"
        subtitle="This navigation is only available when move mode is activated."
        onClose={onClose}
        renderFooter={() => (
          <Dialog.Footer sx={{display: 'flex'}}>
            <KeyboardSpecificInstructionsFooter onClose={onClose} />
          </Dialog.Footer>
        )}
      >
        <Box as="table" sx={instructionContainerSx}>
          <Box className="sr-only" as="thead">
            <Box as="tr">
              <Text as="th" sx={{fontWeight: 'bold'}}>
                Action
              </Text>
              <Text as="th" sx={{fontWeight: 'bold'}}>
                Keyboard Shortcut
              </Text>
            </Box>
          </Box>
          <Box as="tbody">
            <Box as="tr" sx={instructionSx}>
              <Text as="td">{DragAndDropResources.cancelDrag}</Text>
              <Box as="td">
                <KeyboardKey keys="esc" />
              </Box>
            </Box>
            <Box as="tr" sx={instructionSx}>
              <Text as="td">{DragAndDropResources.moveDown}</Text>
              <Box as="td">
                <KeyboardKey keys="down" />
              </Box>
            </Box>
            <Box as="tr" sx={instructionSx}>
              <Text as="td">{DragAndDropResources.moveUp}</Text>
              <Box as="td">
                <KeyboardKey keys="up" />
              </Box>
            </Box>
            <Box as="tr" sx={{...instructionSx, borderBottom: 'none'}}>
              <Text as="td">{DragAndDropResources.endDrag}</Text>
              <Box as="td">
                <KeyboardKey keys="enter" />
                <Text> or </Text>
                <KeyboardKey keys="space" />
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </Box>
  )
}

try{ KeyboardSpecificInstructionsFooter.displayName ||= 'KeyboardSpecificInstructionsFooter' } catch {}
try{ KeyboardSpecificInstructionsModal.displayName ||= 'KeyboardSpecificInstructionsModal' } catch {}